import { Card, CardAction, Text } from '@lawpilots/design-system'
import { useTranslation } from 'react-i18next'

import { CertificateDto } from '@/services/api/tenant/certificates'
import { shortDateByLocale } from '@/utils'

type MyCertificateCardProps = {
  certificate: CertificateDto
  onView: (certificate: CertificateDto) => void
  onDownload: (certificate: CertificateDto) => void
  /**
   * The mobile card (Figma My Account 2017:11886): a smaller date and Download alone. The design
   * puts tablet on the full card, so the preview goes with the roomy form.
   */
  compact: boolean
}

/**
 * One earned certificate: the course title, the date it was issued, then View and Download. Both
 * actions render in the secondary variant so they match the design's pair of outline buttons - the
 * card's slot mapping would otherwise fill the primary one.
 */
export const MyCertificateCard = ({
  certificate,
  onView,
  onDownload,
  compact,
}: MyCertificateCardProps): JSX.Element => {
  const { t, i18n } = useTranslation('general')

  // No url means the PDF has not been generated yet. The design does not cover this case, so the
  // pre-redesign treatment is kept: the card stays, both actions are disabled and the issue date
  // gives way to an explanation.
  const isPending = !certificate.url
  const secondaryLine = isPending ? t('cert_not_available') : shortDateByLocale(certificate.obtained_on, i18n.language)

  const viewAction: CardAction = {
    label: t('cert_view'),
    icon: 'page',
    variant: 'secondary',
    disabled: isPending,
    onClick: () => onView(certificate),
  }

  const downloadAction: CardAction = {
    label: t('cert_download'),
    // The mobile frame draws this button as a label alone (Figma 2017:11902).
    icon: compact ? undefined : 'download',
    variant: 'secondary',
    disabled: isPending,
    onClick: () => onDownload(certificate),
  }

  return (
    <Card
      data-testid="my-certificate-card"
      role="group"
      aria-label={t('my_certificates_card_label', { course: certificate.course_title })}
      title={certificate.course_title}
      graphic="certificates"
      density={compact ? 'compact' : undefined}
      primaryAction={compact ? downloadAction : viewAction}
      secondaryAction={compact ? undefined : downloadAction}>
      {secondaryLine && (
        <Text variant={compact ? 'secondary-reading' : 'body-reading'} color="secondary">
          {secondaryLine}
        </Text>
      )}
    </Card>
  )
}
